// @ts-check
import { useMemo, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MBModal from '../../../../shared/components/modals/MBModal';

/**
 * @typedef {import('../types').ApiCallLogRow} ApiCallLogRow
 */

/**
 * Pretty-prints a raw request body string as JSON when possible, falling
 * back to the raw string (e.g. for payloads truncated by the backend,
 * which are no longer valid JSON).
 *
 * @param {string | null | undefined} raw
 * @returns {string}
 */
export const formatRequestBody = (raw) => {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

/**
 * Icon button + modal for viewing the raw API request body captured for a
 * health monitor log entry. Renders nothing when there is no request body
 * to show.
 *
 * @param {{ requestBody?: ApiCallLogRow['requestBody'] }} props
 */
const RequestBodyButton = ({ requestBody }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const formatted = useMemo(
    () => formatRequestBody(requestBody),
    [requestBody]
  );

  if (!requestBody) {
    return null;
  }

  const handleOpen = (event) => {
    event.stopPropagation();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCopied(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formatted);
      setCopied(true);
    } catch {
      // Clipboard API unavailable or denied — ignore silently.
    }
  };

  return (
    <>
      <Tooltip title='View Raw Request Body'>
        <IconButton
          size='small'
          aria-label='view-raw-request-body'
          onClick={handleOpen}
        >
          <CodeIcon fontSize='small' />
        </IconButton>
      </Tooltip>

      {open && (
        <MBModal
          open={open}
          onClose={handleClose}
          title='Raw Request Body'
          maxWidth='md'
          actions={
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
              <IconButton
                aria-label='copy-request-body'
                size='small'
                onClick={handleCopy}
              >
                <ContentCopyIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          }
        >
          <Box
            component='pre'
            aria-label='request-body-content'
            sx={{
              fontFamily: "Consolas, Menlo, Monaco, 'Courier New', monospace",
              fontSize: 12,
              lineHeight: 1.4,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              backgroundColor: 'grey.100',
              color: 'common.black',
              borderRadius: 1,
              p: 1.5,
              m: 0,
              maxHeight: '60vh',
              overflow: 'auto',
            }}
          >
            {formatted}
          </Box>
        </MBModal>
      )}
    </>
  );
};

export default RequestBodyButton;
